// Info tab — the chart-supplement-style block.
//
// Renders the public-domain NASR APT.txt data we ship in the airport
// detail blob: frequencies, identifiers, location, plus a Field
// Operations section with manager contact, ownership, fuel grades,
// services, lighting schedules, attendance, customs/landing-rights
// flags, and free-form NASR remarks. Anything not published by NASR
// (FBO directory, full d-CS special procedures, fuel hours) stays in
// the subscription-required stub at the bottom.

import type { AirportDetail, AirportOps } from '../../lib/airports';
import FrequencyIcon from '../FrequencyIcon';
import { formatLatLonDMM, formatLatLonDMS } from '../../lib/coords';
import {
  decodeOwnership,
  decodeFacilityUse,
  decodeWindIndicator,
  decodeSegmentedCircle,
  decodeBeaconColor,
  decodeTransientStorage,
  decodeOtherServices,
  decodeLightingSchedule,
  decodeActivationDate,
  decodePhone,
  decodeNpiasCodes,
  decodeRmkLabel,
} from '../airportOpsDecoder';

const FREQ_PRIORITY: Record<string, number> = {
  // Lower number = higher row in the list.
  'CTAF': 1,
  'UNIC': 2,
  'TWR': 3,
  'GND': 4,
  'CLNC': 5,
  'ATIS': 6,
  'AWOS': 7,
  'ASOS': 8,
  'APP': 9,
  'DEP': 10,
  'CNTR': 11,
  'RDO': 12,
};

export function InfoTab({ detail }: { detail: AirportDetail }) {
  const freqs = [...(detail.frequencies || [])].sort((a, b) => {
    const pa = FREQ_PRIORITY[a.type] ?? 50;
    const pb = FREQ_PRIORITY[b.type] ?? 50;
    return pa - pb;
  });

  return (
    <div className="px-4 py-3 space-y-4 text-sm">
      <Section title="Frequencies">
        {freqs.length === 0 ? (
          <Empty>No frequencies on file.</Empty>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-[10px] tracking-widest text-cockpit-400">
              <tr>
                <th className="text-left py-1">TYPE</th>
                <th className="text-left py-1">DESCRIPTION</th>
                <th className="text-right py-1">FREQ</th>
              </tr>
            </thead>
            <tbody>
              {freqs.map((f, i) => (
                <tr key={`${f.type}-${i}`} className="border-t border-cockpit-700/60">
                  <td className="py-1.5 font-mono text-cockpit-200">
                    <span className="inline-flex items-center gap-1.5">
                      <FrequencyIcon type={f.type} className="h-4 w-4 text-ember-300" />
                      <span>{f.type}</span>
                    </span>
                  </td>
                  <td className="py-1.5 text-cockpit-200">{f.description || '—'}</td>
                  <td className="py-1.5 font-mono text-cockpit-100 text-right">{f.frequency_mhz}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      <Section title="Identifiers">
        <KvRow k="ICAO" v={detail.airport.icao || detail.airport.ident} />
        <KvRow k="IATA" v={detail.airport.iata} />
        <KvRow k="GPS" v={detail.airport.gps} />
        <KvRow k="Local" v={detail.airport.local} />
      </Section>

      <Section title="Location">
        {/* DMM (Forest Service / fire-aviation standard) is the primary
            user-facing format per Wiley feedback 2026-05-09. DMS shown
            as a secondary line for pilots who prefer the older sectional
            format. Raw decimal-degrees is one click away in NavaidDetail
            for anyone copy-pasting into another tool. */}
        <KvRow k="Coords (DMM)" v={formatLatLonDMM(detail.airport.lat, detail.airport.lon)} />
        <KvRow k="Coords (DMS)" v={formatLatLonDMS(detail.airport.lat, detail.airport.lon)} />
        <KvRow k="City" v={detail.airport.city} />
        <KvRow k="Region" v={detail.airport.iso_region} />
        <KvRow k="Country" v={detail.airport.iso_country} />
      </Section>

      <FieldOperationsSection detail={detail} />

      <SubscriptionStub />
    </div>
  );
}

// ─── Field operations — NASR-derived structured sections ────────────

function FieldOperationsSection({ detail }: { detail: AirportDetail }) {
  const ops = detail.ops;
  if (!ops) {
    // Non-US airport (OurAirports fallback) — no NASR ops data.
    return null;
  }

  // Decode codes ahead of render so the JSX stays readable.
  const ap = detail.airport;
  const owner = decodeOwnership(ap.ownership);
  const facUse = decodeFacilityUse(ap.facilityUse);
  const lightSched = decodeLightingSchedule(ap.lightingSchedule);
  const beaconSched = decodeLightingSchedule(ops.beaconLightingSchedule);
  const windInd = decodeWindIndicator(ops.windIndicator);
  const segCircle = decodeSegmentedCircle(ops.segmentedCircle);
  const beaconColor = decodeBeaconColor(ops.beaconColor);
  const activation = decodeActivationDate(ops.activationDate);
  const storage = decodeTransientStorage(ops.transientStorage);
  const services = decodeOtherServices(ops.otherServices);
  const npias = decodeNpiasCodes(ops.npiasFedAgreementCodes);

  const fuelTypes = ap.fuelTypes ?? [];
  const tower = ap.hasTower;

  return (
    <>
      {/* Manager + owner */}
      {(ops.manager || ops.owner) && (
        <Section title="Manager · Owner">
          {ops.manager && (
            <ContactBlock label="Manager" contact={ops.manager} />
          )}
          {ops.owner && ops.manager && (
            <div className="border-t border-cockpit-700/60 my-1.5" />
          )}
          {ops.owner && (
            <ContactBlock label="Owner" contact={ops.owner} />
          )}
        </Section>
      )}

      {/* Fuel */}
      <Section title="Fuel">
        {fuelTypes.length === 0 ? (
          <Empty>No fuel reported.</Empty>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {fuelTypes.map((f) => (
              <span key={f} className="bg-ember-700/20 border border-ember-700/60 text-ember-300 rounded px-2 py-0.5 text-xs font-mono">
                {f}
              </span>
            ))}
          </div>
        )}
        <div className="mt-2 space-y-0">
          {ops.contractFuel != null && (
            <KvRow k="Contract fuel" v={ops.contractFuel ? 'Yes' : 'No'} />
          )}
        </div>
        <p className="text-[10px] text-cockpit-400 mt-2 leading-snug">
          Fuel grades + contract fuel from FAA NASR. Hours of operation
          and dispenser locations require a paid FBO data source (see
          below).
        </p>
      </Section>

      {/* Services */}
      {(ops.airframeRepair || ops.engineRepair || ops.bottledOxygen ||
        ops.bulkOxygen || storage.length > 0 || services.length > 0) && (
        <Section title="Services">
          <KvRow k="Airframe repair" v={prettyRepair(ops.airframeRepair)} />
          <KvRow k="Engine repair"   v={prettyRepair(ops.engineRepair)} />
          <KvRow k="Bottled O₂"      v={prettyOxygen(ops.bottledOxygen)} />
          <KvRow k="Bulk O₂"         v={prettyOxygen(ops.bulkOxygen)} />
          {storage.length > 0 && (
            <KvRow k="Storage" v={storage.join(', ')} />
          )}
          {services.length > 0 && (
            <div className="pt-1.5 border-t border-cockpit-700/60 mt-1.5">
              <div className="text-[10px] text-cockpit-400 mb-1">Other services</div>
              <div className="flex flex-wrap gap-1">
                {services.map((s) => (
                  <span key={s} className="bg-cockpit-700/60 border border-cockpit-600 rounded px-1.5 py-0.5 text-[11px] text-cockpit-200">
                    {s}
                  </span>
                ))}
              </div>
            </div>
          )}
        </Section>
      )}

      {/* Lighting + visual aids */}
      {(lightSched || beaconSched || windInd || segCircle || beaconColor) && (
        <Section title="Lighting · Visual aids">
          <KvRow k="Airport lighting" v={lightSched} />
          <KvRow k="Beacon"           v={beaconSched} />
          <KvRow k="Beacon color"     v={beaconColor} />
          <KvRow k="Wind indicator"   v={windInd} />
          <KvRow k="Segmented circle" v={segCircle} />
        </Section>
      )}

      {/* Hours / attendance */}
      {ops.attendance && ops.attendance.length > 0 && (
        <Section title="Attendance">
          <ul className="space-y-0.5 text-xs">
            {ops.attendance.map((line, i) => (
              <li key={i} className="font-mono text-cockpit-200">{line}</li>
            ))}
          </ul>
        </Section>
      )}

      {/* Federal status / customs / landing rights */}
      {(owner || facUse || ops.customsAirportOfEntry != null ||
        ops.customsLandingRights != null || ops.jointUseCivilAtMilitary ||
        ops.militaryLandingRights || ops.landingFeeNonCommercial != null ||
        ops.arffCertification || npias.length > 0 || activation ||
        ops.sectionalChart || ops.trafficPatternAltitudeAgl != null) && (
        <Section title="Field operations">
          <KvRow k="Ownership"       v={owner} />
          <KvRow k="Facility use"    v={facUse} />
          <KvRow k="Tower"           v={tower == null ? null : tower ? 'Yes (ATCT on field)' : 'No'} />
          <KvRow k="TPA (AGL)"       v={ops.trafficPatternAltitudeAgl ? `${ops.trafficPatternAltitudeAgl} ft` : null} />
          <KvRow k="Customs · airport of entry" v={ops.customsAirportOfEntry == null ? null : ops.customsAirportOfEntry ? 'Yes' : 'No'} />
          <KvRow k="Customs · landing rights"   v={ops.customsLandingRights == null ? null : ops.customsLandingRights ? 'Yes' : 'No'} />
          <KvRow k="Joint use (civil at mil)"   v={ops.jointUseCivilAtMilitary ? 'Yes' : null} />
          <KvRow k="Military landing rights"    v={ops.militaryLandingRights ? 'Yes' : null} />
          <KvRow k="Landing fee (non-comm)"     v={ops.landingFeeNonCommercial == null ? null : ops.landingFeeNonCommercial ? 'Yes' : 'No'} />
          <KvRow k="ARFF certification" v={ops.arffCertification} />
          <KvRow k="Sectional chart"  v={ops.sectionalChart} />
          <KvRow k="Activation date"  v={activation} />
          {npias.length > 0 && (
            <div className="pt-1.5 border-t border-cockpit-700/60 mt-1.5">
              <div className="text-[10px] text-cockpit-400 mb-1">Federal agreements</div>
              <ul className="list-disc list-inside text-[11px] text-cockpit-200 space-y-0.5">
                {npias.map((s) => <li key={s}>{s}</li>)}
              </ul>
            </div>
          )}
        </Section>
      )}

      {/* Remarks — closest thing NASR ships to d-CS "special procedures" */}
      {ops.remarks && ops.remarks.length > 0 && (
        <Section title="Remarks">
          <ul className="space-y-2 text-xs">
            {ops.remarks.map((r, i) => (
              <li key={i}>
                <div className="text-[10px] tracking-widest text-cockpit-400">
                  {decodeRmkLabel(r.key)}
                </div>
                <div className="text-cockpit-200 leading-snug">{r.text}</div>
              </li>
            ))}
          </ul>
        </Section>
      )}
    </>
  );
}

function ContactBlock({ label, contact }: {
  label: string;
  contact: NonNullable<AirportOps['manager']>;
}) {
  const phone = decodePhone(contact.phone);
  return (
    <div>
      <div className="text-[10px] tracking-widest text-cockpit-400 mb-0.5">{label.toUpperCase()}</div>
      {contact.name && <div className="text-cockpit-100">{contact.name}</div>}
      {contact.cityStateZip && (
        <div className="text-[11px] text-cockpit-300">{contact.cityStateZip}</div>
      )}
      {phone && (
        <a href={`tel:${phone.replace(/[^\d+]/g, '')}`} className="text-[11px] text-ember-400 hover:text-ember-300 font-mono">
          {phone}
        </a>
      )}
    </div>
  );
}

function prettyRepair(v: string | undefined | null): string | null {
  if (!v) return null;
  const u = v.toUpperCase();
  if (u === 'NONE') return 'None';
  if (u === 'MAJOR') return 'Major';
  if (u === 'MINOR') return 'Minor';
  return v;
}

function prettyOxygen(v: string | undefined | null): string | null {
  if (!v) return null;
  const u = v.toUpperCase();
  if (u === 'NONE') return 'None';
  if (u === 'HIGH') return 'High pressure';
  if (u === 'LOW') return 'Low pressure';
  if (u === 'HIGH/LOW') return 'High + low pressure';
  return v;
}

function SubscriptionStub() {
  return (
    <section>
      <h3 className="text-[10px] tracking-widest text-cockpit-400 mb-1">FBOS · D-CS EXTRAS</h3>
      <div className="bg-cockpit-700/20 border border-dashed border-cockpit-600 rounded-md px-3 py-2 text-xs text-cockpit-300">
        <p className="leading-snug">
          FBO directory (per-FBO phone / freq / hours), fuel dispenser
          hours, and the full FAA Digital Chart Supplement special
          procedures require a paid data source. Currently not wired —
          contact the operator if you want a subscription added.
        </p>
        <p className="mt-2 text-[10px] text-cockpit-400">
          Source: AirNav (FBO directory) + FAA d-CS PDF (special procedures).
          See <span className="font-mono">docs/airport-detail-spec.md</span>.
        </p>
      </div>
    </section>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h3 className="text-[10px] tracking-widest text-cockpit-400 mb-1">{title.toUpperCase()}</h3>
      <div className="bg-cockpit-700/30 border border-cockpit-700 rounded-md px-3 py-2">{children}</div>
    </section>
  );
}

function KvRow({ k, v }: { k: string; v?: string | number | null }) {
  if (v == null || v === '') return null;
  return (
    <div className="flex items-center justify-between py-0.5 text-xs">
      <span className="text-cockpit-300">{k}</span>
      <span className="font-mono text-cockpit-100 text-right ml-2">{String(v)}</span>
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <div className="text-xs text-cockpit-400 italic">{children}</div>;
}
